import type {
  HttpClient,
  HttpClientConfig,
  RequestOptions,
  ServiceClient,
  ServiceName,
} from "./types";
import { AuthError, HttpError } from "./errors";
import { buildUrl } from "./url";
import { getTokenAsync } from "@coworker/app/src/hooks/API/getAuthToken";
import { reportToSentry } from "./adapters/sentry";
import { ALL_SERVICES, resolveBaseUrl } from "./adapters/services";

// Read version once at module load
const CLIENT_VERSION: string =
  (import.meta.env["VITE_APP_VERSION"] as string) ?? "unknown";

async function extractErrorBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get("Content-Type") ?? "";
  try {
    if (contentType.includes("application/json")) {
      return await response.json();
    }
    return await response.text();
  } catch {
    return undefined;
  }
}

// ponytail: classifies AbortError by name on any non-null object, not just
// Error instances - DOMException (browser fetch aborts) and some
// environments' plain-object aborts both just carry a "name" field, and
// requiring `instanceof Error` would miss those, reporting them as
// network_failure noise instead of the intentional cancellation they are.
function isAbortError(err: unknown): boolean {
  return (
    typeof err === "object" &&
    err !== null &&
    (err as { name?: unknown }).name === "AbortError"
  );
}

async function fetchWithSentryReporting(
  url: string,
  init: RequestInit,
  service: ServiceName
): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (err) {
    if (isAbortError(err)) throw err; // already globally ignored via Sentry ignoreErrors
    reportToSentry(err as Error, {
      url,
      method: init.method ?? "GET",
      service,
      kind: "network_failure",
    });
    throw err;
  }
}

export function createHttpClient(config?: HttpClientConfig): HttpClient {
  const getToken = config?.auth ?? getTokenAsync;

  async function request<T>(
    service: ServiceName,
    method: string,
    path: string,
    options: RequestOptions = {}
  ): Promise<T> {
    const token = await getToken();
    if (!token || typeof token !== "string") {
      throw new AuthError("No valid authentication token available");
    }

    const baseUrl = resolveBaseUrl(service);
    const url = buildUrl(baseUrl, path, options.params);

    //CWH does not allow X-Client-Version in the header so we need to remove it before making the call
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      ...(service !== "cwh" ? { "X-Client-Version": CLIENT_VERSION } : {}),
      ...options.headers,
    };

    const isFormData = options.body instanceof FormData;

    if (options.body && !isFormData) {
      headers["Content-Type"] = "application/json";
    }

    const response = await fetchWithSentryReporting(
      url,
      {
        method,
        headers,
        body: isFormData
          ? (options.body as FormData)
          : options.body
            ? JSON.stringify(options.body)
            : undefined,
        signal: options.signal,
      },
      service
    );

    if (!response.ok) {
      const body = await extractErrorBody(response);
      const error = new HttpError(response.status, response.statusText, body);

      // Auto-report server errors and auth denials to Sentry
      if (response.status >= 500) {
        reportToSentry(error, {
          url,
          method,
          service,
          kind: "server_error",
          status: response.status,
        });
      } else if (response.status === 401 || response.status === 403) {
        reportToSentry(error, {
          url,
          method,
          service,
          kind: "auth_denied",
          status: response.status,
        });
      }

      throw error;
    }

    if (options.responseType === "blob") {
      return (await response.blob()) as T;
    }

    if (options.responseType === "text") {
      return (await response.text()) as T;
    }

    const text = await response.text();
    if (!text) return undefined as T;
    const contentType = response.headers.get("Content-Type") ?? "";
    if (contentType.includes("application/json")) {
      return JSON.parse(text) as T;
    }
    return text as unknown as T;
  }

  const client = {} as HttpClient;

  for (const service of ALL_SERVICES) {
    const svc: ServiceClient = {
      get: <T>(path: string, opts?: RequestOptions) =>
        request<T>(service, "GET", path, opts),
      post: <T>(path: string, opts?: RequestOptions) =>
        request<T>(service, "POST", path, opts),
      put: <T>(path: string, opts?: RequestOptions) =>
        request<T>(service, "PUT", path, opts),
      patch: <T>(path: string, opts?: RequestOptions) =>
        request<T>(service, "PATCH", path, opts),
      delete: <T>(path: string, opts?: RequestOptions) =>
        request<T>(service, "DELETE", path, opts),
      upload: <T>(path: string, body: FormData) =>
        request<T>(service, "POST", path, { body: body as unknown }),
    };
    client[service] = svc;
  }

  return client;
}
